import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const CURRENCY_PREFERENCE_STORAGE_KEY = 'ibe.currency.preference';

/**
 * Guest-facing currency options.
 *
 * This is the single source of truth for the selector values. The label
 * includes a symbol hint; the value is the ISO 4217 code the pricing engine
 * expects. Order is not significant for correctness, but the first option
 * is the current fallback while a site/tenant default is not yet available.
 */
export const CURRENCY_OPTIONS = [
  { value: 'AUD', label: 'AUD ($)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
] as const;

const SUPPORTED_CURRENCY_CODES: readonly string[] = CURRENCY_OPTIONS.map((option) => option.value);

/** Fallback currency while site/tenant default currency is not wired. */
const FALLBACK_CURRENCY = 'AUD';

export interface CurrencyContextValue {
  readonly currency: string;
  readonly setCurrency: (currency: string) => void;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function isSupportedCurrency(currency: string): boolean {
  return SUPPORTED_CURRENCY_CODES.includes(currency);
}

function resolveCurrencyPreference(stored: string | null): string {
  if (stored !== null && isSupportedCurrency(stored)) return stored;
  return FALLBACK_CURRENCY;
}

function readCurrencyPreference(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(CURRENCY_PREFERENCE_STORAGE_KEY);
  } catch {
    // localStorage can be disabled or throw; the preference simply won't persist.
    return null;
  }
}

function writeCurrencyPreference(currency: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CURRENCY_PREFERENCE_STORAGE_KEY, currency);
  } catch {
    // localStorage can be disabled or full; the in-memory preference still works.
  }
}

function readCurrencyFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('currency');
  } catch {
    return null;
  }
}

function resolveInitialCurrency(provided: string | undefined): string {
  if (provided !== undefined && isSupportedCurrency(provided)) return provided;
  const fromUrl = readCurrencyFromUrl();
  if (fromUrl !== null && isSupportedCurrency(fromUrl)) return fromUrl;
  return resolveCurrencyPreference(readCurrencyPreference());
}

export function CurrencyProvider({
  children,
  initialCurrency,
}: {
  readonly children: ReactNode;
  readonly initialCurrency?: string | undefined;
}): React.JSX.Element {
  const [currency, setCurrencyState] = useState<string>(() =>
    resolveInitialCurrency(initialCurrency),
  );

  useEffect(() => {
    writeCurrencyPreference(currency);
  }, [currency]);

  const setCurrency = useCallback((next: string) => {
    setCurrencyState(resolveCurrencyPreference(next));
  }, []);

  const value = useMemo<CurrencyContextValue>(
    () => ({ currency, setCurrency }),
    [currency, setCurrency],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (context === null) {
    return { currency: FALLBACK_CURRENCY, setCurrency: () => undefined };
  }
  return context;
}
